import { type FC, type FocusEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	List,
	ListItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useToast } from "@packsize/toast";
import type { PackNetServerSettings, User } from "@packsize/identity-api-sdk";
import CloseIcon from "@mui/icons-material/Close";
import { useIdentityClient } from "@packsize/authentication";

import { isValidEmail, isValidPassword } from "@/schemas/authentication";
import { Loading } from "@/components/loading";
import { useUsers } from "@/hooks/user";
import { useAuthenticationMethod } from "@/hooks/use-authentication-method";
import { useRoles } from "@/hooks/roles";

import { useUserEdit } from "./hooks/use-user-edit";

interface ValidationErrors {
	userName?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	password?: string;
	password2?: string;
	assignedRoles?: string;
	addOrUpdate?: string;
}

interface NewUserProps {
	readonly closeModal: () => void;
	readonly user?: User;
}

const NewUser: FC<NewUserProps> = ({ closeModal, user }) => {
	const { client: identityApi } = useIdentityClient();
	const intl = useIntl();
	const { toastSuccess, toastError } = useToast();
	const [errors, setErrors] = useState<ValidationErrors>({});
	const [settings, setSettings] = useState<Partial<PackNetServerSettings>>({});
	const { userEdit, setUserEdit } = useUserEdit(user);

	const { refetch: refetchUsers } = useUsers();
	const { data: authMethod } = useAuthenticationMethod();
	const shouldAllowUserEdits = authMethod?.name !== "Saml";
	const { data: roles, isLoading: isLoadingRoles } = useRoles();

	const validatePhoneForE164 = (phoneNumber: string): boolean => {
		const regEx = /^\+[1-9]\d{10,14}$/;
		return regEx.test(phoneNumber);
	};

	const disablePasswordInputs = settings?.requirePacksizeOkta || settings?.disablePasswordLogin;

	useEffect(() => {
		if (Object.keys(settings).length > 0) {
			setUserEdit({
				forcePasswordReset: userEdit?.forcePasswordReset ?? (disablePasswordInputs ? false : true),
			});
		}
	}, [disablePasswordInputs, settings, setUserEdit, userEdit?.forcePasswordReset]);

	const generatePassword = (
		length = 20,
		validChars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$",
	): string =>
		Array.from(crypto.getRandomValues(new Uint32Array(length)))
			.map((x) => validChars[x % validChars.length])
			.join("") + "Aa0!";

	const validate = (): ValidationErrors => {
		const newErrors: ValidationErrors = {};
		const isEdit = userEdit.id;

		if (!userEdit.userName) {
			newErrors.userName = intl.formatMessage({ id: "Username is required", defaultMessage: "Username is required" });
		}
		if (!userEdit.firstName) {
			newErrors.firstName = intl.formatMessage({
				id: "First Name is required",
				defaultMessage: "First Name is required",
			});
		}
		if (!userEdit.lastName) {
			newErrors.lastName = intl.formatMessage({ id: "Last Name is required", defaultMessage: "Last Name is required" });
		}
		if (userEdit.phoneNumber && !validatePhoneForE164(userEdit.phoneNumber)) {
			newErrors.phoneNumber = intl.formatMessage({
				id: "Phone Number is Invalid: required format +12125551212",
				defaultMessage: "Phone Number is Invalid: required format +12125551212",
			});
		}

		if (!disablePasswordInputs) {
			const password = userEdit.password;
			const password2 = userEdit.password2;

			if (!isEdit || password || password2) {
				if (!password) {
					newErrors.password = intl.formatMessage({
						id: "Password is required",
						defaultMessage: "Password is required",
					});
				}
				if (!password2) {
					newErrors.password2 = intl.formatMessage({
						id: "Password confirmation is required",
						defaultMessage: "Password confirmation is required",
					});
				}
				if (password !== password2) {
					newErrors.password2 = intl.formatMessage({
						id: "Passwords must match",
						defaultMessage: "Passwords must match",
					});
				}

				if (password && !isValidPassword(password)) {
					newErrors.password = intl.formatMessage({
						id: "Password is not strong enough",
						defaultMessage: "Password is not strong enough",
					});
				}
			}
		}

		if (userEdit.userName && settings.requireEmailAsUsername && !isValidEmail(userEdit.userName)) {
			newErrors.userName = intl.formatMessage({ id: "Email must be valid", defaultMessage: "Email must be valid" });
		}

		if (!userEdit.assignedRoles || userEdit.assignedRoles.length === 0) {
			newErrors.assignedRoles = intl.formatMessage({
				id: "At least one role must be selected",
				defaultMessage: "At least one role must be selected",
			});
		}

		setErrors(newErrors);
		return newErrors;
	};

	useEffect(() => {
		identityApi.getSettings().then((data) => {
			setSettings(data.data ?? {});
		});
	}, [identityApi]);

	const close = (): void => {
		try {
			closeModal();
		} catch (err) {
			console.error(err);
		}
	};

	const saveAndClose = async (): Promise<void> => {
		const validationErrors = validate();

		let res: { success: boolean; status?: number } = {
			success: true,
		};
		if (Object.keys(validationErrors).length) return;

		let user = userEdit;

		if (disablePasswordInputs) {
			const password = generatePassword();
			user = { ...userEdit, password, password2: password };
		}

		if (settings.requireEmailAsUsername && user.userName && !isValidEmail(user.userName)) {
			setErrors({
				...validationErrors,
				userName: intl.formatMessage({ id: "Email must be valid", defaultMessage: "Email must be valid" }),
			});
			return;
		}

		const isUpdate = !!user.id;

		if (user.id && user.password) {
			const result = await identityApi.updateUserWithId(user as User);
			if (!result.success) res = result;
		} else if (user.id) {
			const result = await identityApi.updateUserWithoutPassword(user as User);
			if (!result.success) res = result;
		} else {
			const result = await identityApi.addUser(user as User);
			if (!result.success) res = result;
		}

		await refetchUsers();

		if (!res.success) {
			if (res.status === 409) {
				const errorMessage = intl.formatMessage(
					{
						id: "Failed to save User: An item already exists with the Username {name}",
						defaultMessage: "Failed to save User: An item already exists with the Username {name}",
					},
					{ name: user.userName },
				);
				await setErrors({
					...validationErrors,
					addOrUpdate: errorMessage,
				});
				toastError({
					message: errorMessage,
				});
			} else {
				await setErrors({
					...validationErrors,
					addOrUpdate: intl.formatMessage({ id: "Failed to save User", defaultMessage: "Failed to save User" }),
				});
				toastError({
					message: intl.formatMessage({ id: "Failed to save User", defaultMessage: "Failed to save User" }),
				});
			}
		} else {
			toastSuccess({
				message: isUpdate
					? intl.formatMessage(
							{
								id: "User {name} updated successfully",
								defaultMessage: "User {name} updated successfully",
							},
							{ name: user.userName },
						)
					: intl.formatMessage(
							{
								id: "User {name} created successfully",
								defaultMessage: "User {name} created successfully",
							},
							{ name: user.userName },
						),
			});
			close();
		}
	};

	if (!settings.id || isLoadingRoles) {
		return <Loading />;
	}

	return (
		<Box
			component="form"
			onSubmit={(event) => event.preventDefault()}
			sx={{ minHeight: "80vh", maxHeight: "90vh", overflow: "auto", bgcolor: "grey.100" }}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 2, bgcolor: "grey.100" }}>
				<Typography variant="h2" sx={{ flexGrow: 1 }}>
					{userEdit.id ? (
						<FormattedMessage id="Edit User" defaultMessage="Edit User" />
					) : (
						<FormattedMessage id="New User" defaultMessage="New User" />
					)}
				</Typography>
				<IconButton onClick={close} data-pendoid="new-user__button__close-new-user">
					<CloseIcon />
				</IconButton>
			</Box>

			<Box sx={{ px: 3, py: 2 }}>
				<Stack spacing={3}>
					{!shouldAllowUserEdits && (
						<Alert severity="info">
							<FormattedMessage id="User Properties Set Via SSO" defaultMessage="User Properties Set Via SSO" />
						</Alert>
					)}

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 6 }}>
							<Stack spacing={2}>
								<TextField
									data-cy="first-name"
									label={intl.formatMessage({ id: "First Name", defaultMessage: "First Name" })}
									placeholder={intl.formatMessage({ id: "First Name", defaultMessage: "First Name" })}
									required
									disabled={!shouldAllowUserEdits}
									value={userEdit.firstName ?? ""}
									onChange={(event) => setUserEdit({ firstName: event.target.value })}
									error={errors.firstName !== undefined}
									helperText={errors.firstName}
									fullWidth
								/>
								<TextField
									data-cy="last-name"
									label={intl.formatMessage({ id: "Last Name", defaultMessage: "Last Name" })}
									placeholder={intl.formatMessage({ id: "Last Name", defaultMessage: "Last Name" })}
									required
									disabled={!shouldAllowUserEdits}
									value={userEdit.lastName ?? ""}
									onChange={(event) => setUserEdit({ lastName: event.target.value })}
									error={errors.lastName !== undefined}
									helperText={errors.lastName}
									fullWidth
								/>
								<TextField
									data-cy="phone-number"
									label={intl.formatMessage({ id: "Phone Number", defaultMessage: "Phone Number" })}
									placeholder={intl.formatMessage({ id: "Phone Number", defaultMessage: "Phone Number" })}
									disabled={!shouldAllowUserEdits}
									value={userEdit.phoneNumber ?? ""}
									onChange={(event) => setUserEdit({ phoneNumber: event.target.value })}
									error={errors.phoneNumber !== undefined}
									helperText={errors.phoneNumber}
									fullWidth
								/>
								{shouldAllowUserEdits === true && (
									<Box>
										<Typography variant="subtitle1">
											<FormattedMessage id="Password Rules:" defaultMessage="Password Rules:" />
										</Typography>
										<List dense disablePadding>
											<ListItem disableGutters>
												<FormattedMessage id="Minimum length of 8" defaultMessage="Minimum length of 8" />
											</ListItem>
											<ListItem disableGutters>
												<FormattedMessage
													id="Must contain lowercase characters"
													defaultMessage="Must contain lowercase characters"
												/>
											</ListItem>
											<ListItem disableGutters>
												<FormattedMessage
													id="Must contain uppercase characters"
													defaultMessage="Must contain uppercase characters"
												/>
											</ListItem>
											<ListItem disableGutters>
												<FormattedMessage id="Must contain numbers" defaultMessage="Must contain numbers" />
											</ListItem>
											<ListItem disableGutters>
												<FormattedMessage id="Must contain symbols" defaultMessage="Must contain symbols" />
											</ListItem>
										</List>
									</Box>
								)}
							</Stack>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Stack spacing={2}>
								{settings.requireEmailAsUsername ? (
									<TextField
										data-cy="email-address"
										label={intl.formatMessage({ id: "Email Address", defaultMessage: "Email Address" })}
										placeholder={intl.formatMessage({ id: "Email Address", defaultMessage: "Email Address" })}
										required
										disabled={!shouldAllowUserEdits}
										value={userEdit.userName ?? ""}
										onChange={(event) => {
											const value = event.target.value;
											setUserEdit({ userName: value });
											if (isValidEmail(value)) {
												setErrors({ ...errors, userName: undefined });
											}
										}}
										onBlur={(event: FocusEvent<HTMLInputElement>) => {
											const value = event.target.value;
											setErrors({
												...errors,
												userName:
													value && !isValidEmail(value)
														? intl.formatMessage({
																id: "Email must be valid",
																defaultMessage: "Email must be valid",
															})
														: undefined,
											});
										}}
										error={errors.userName !== undefined}
										helperText={errors.userName}
										fullWidth
									/>
								) : (
									<TextField
										data-cy="user-name"
										label={intl.formatMessage({ id: "Username", defaultMessage: "Username" })}
										placeholder={intl.formatMessage({ id: "Username", defaultMessage: "Username" })}
										required
										disabled={!shouldAllowUserEdits}
										value={userEdit.userName ?? ""}
										onChange={(event) => setUserEdit({ userName: event.target.value })}
										error={errors.userName !== undefined}
										helperText={errors.userName}
										fullWidth
									/>
								)}
								{disablePasswordInputs === true && shouldAllowUserEdits === true && (
									<Alert severity="info">
										<FormattedMessage id="Passwords Set Via SSO" defaultMessage="Passwords Set Via SSO" />
									</Alert>
								)}
								<TextField
									label={intl.formatMessage({ id: "Password", defaultMessage: "Password" })}
									placeholder={intl.formatMessage({ id: "Enter User Password", defaultMessage: "Enter User Password" })}
									type="password"
									disabled={disablePasswordInputs || !shouldAllowUserEdits}
									required
									onChange={(event) => setUserEdit({ password: event.target.value })}
									error={errors.password !== undefined}
									helperText={errors.password}
									fullWidth
								/>
								<TextField
									label={intl.formatMessage({ id: "Confirm Password", defaultMessage: "Confirm Password" })}
									placeholder={intl.formatMessage({ id: "Confirm Password", defaultMessage: "Confirm Password" })}
									type="password"
									disabled={disablePasswordInputs || !shouldAllowUserEdits}
									required
									onChange={(event) => setUserEdit({ password2: event.target.value })}
									error={errors.password2 !== undefined}
									helperText={errors.password2}
									fullWidth
								/>
							</Stack>
						</Grid>
					</Grid>

					{!disablePasswordInputs && <Divider />}

					{!disablePasswordInputs && (
						<FormControlLabel
							control={
								<Checkbox
									checked={userEdit.forcePasswordReset === true}
									disabled={!shouldAllowUserEdits}
									onChange={(event) => setUserEdit({ forcePasswordReset: event.target.checked })}
								/>
							}
							label={
								<FormattedMessage
									id="Require password reset on next login"
									defaultMessage="Require password reset on next login"
								/>
							}
						/>
					)}

					<Divider />

					<Box>
						<Typography variant="subtitle1" component="label">
							<FormattedMessage id="Roles" defaultMessage="Roles" />
						</Typography>
						{errors.assignedRoles ? <Alert severity="error">{errors.assignedRoles}</Alert> : null}
					</Box>
					{roles?.length === 0 ? (
						<Alert severity="error">
							<FormattedMessage
								id="No roles available. Please create at least one role before creating or editing users."
								defaultMessage="No roles available. Please create at least one role before creating or editing users."
							/>
						</Alert>
					) : (
						<Grid container spacing={1}>
							{roles?.map((role) => (
								<Grid key={role.id} size={{ xs: 12, sm: 6 }}>
									<FormControlLabel
										control={
											<Checkbox
												value={role.id}
												checked={userEdit.assignedRoles?.includes(role.id ?? "") === true}
												disabled={!shouldAllowUserEdits}
												onChange={(event) => {
													const value = event.target.value;
													const currentRoles = userEdit.assignedRoles ?? [];
													const assignedRoles = event.target.checked
														? [...currentRoles, value]
														: currentRoles.filter((assignedRole) => assignedRole !== value);
													setUserEdit({ assignedRoles });
													if (assignedRoles.length > 0) {
														setErrors({ ...errors, assignedRoles: undefined });
													}
												}}
											/>
										}
										label={role.name}
									/>
								</Grid>
							))}
						</Grid>
					)}
					{errors.addOrUpdate ? <Alert severity="error">{errors.addOrUpdate}</Alert> : null}
				</Stack>
			</Box>
			<Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2, bgcolor: "grey.100" }}>
				<Button
					variant="contained"
					onClick={saveAndClose}
					disabled={roles?.length === 0 || !shouldAllowUserEdits}
					data-cy="save-button"
					data-pendoid="new-user__button__save-new-user"
				>
					<FormattedMessage id="Save" defaultMessage="Save" />
				</Button>
			</Box>
		</Box>
	);
};

export default NewUser;
